import { type FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useStartYourApplicationEditableAsset from '@core/hooks/useStartYourApplicationEditableAsset';
import { selectViewCorrelationIdMap } from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import { validatePhone } from '@core/services/leadDelivery';

type DualCtaTestProps = {
  schoolId: string;
  className?: string;
};

type ReturnType = {
  DualCtaTestComponent: FC<DualCtaTestProps>;
  shouldApplyWrapperStyles: boolean;
};

type PrefillFields = {
  FNAME: string;
  LNAME: string;
  EMAIL: string;
  PHONE: string; // already formatted per strategy
  ZIP: string;
};

const getFormData = async (formInputs: any, useNumericPhone = false): Promise<PrefillFields> => {
  const { firstName, lastName, email, phone, zipCode } = formInputs;
  const phoneValidation = await validatePhone(phone.value);
  const formattedPhone = phoneValidation?.phoneNumber ?? phone.value;
  const phoneToUse = useNumericPhone ? formattedPhone.replace(/\D/g, '') : formattedPhone;

  return {
    FNAME: firstName.value,
    LNAME: lastName.value,
    EMAIL: email.value,
    PHONE: phoneToUse,
    ZIP: zipCode?.value || '',
  };
};

const buildUrlFromParams = (baseUrl: string, params: Record<string, string | null | undefined>): string => {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    if (value === '' && key !== 'SYSTUDENTID') return; // keep SYSTUDENTID=''
    url.searchParams.set(key, value);
  });
  return url.toString();
};

// minimal strategy table per school
const SCHOOL_STRATEGY: Record<
  string,
  {
    useNumericPhone: boolean;
    makeParams: (d: PrefillFields, programCode?: string) => Record<string, string | null | undefined>;
  }
> = {
  // Purdue Global(451): numeric phone, special params
  '451': {
    useNumericPhone: true,
    makeParams: (d) => ({
      FNAME: d.FNAME,
      LNAME: d.LNAME,
      EMAIL: d.EMAIL,
      PHONE: d.PHONE,
      SYSTUDENTID: '',
      SOURCEAPP: 'affiliate',
    }),
  },
  // Lynn (220) & Wake Forest SPS (1025): keep symbols in phone, include PROGRAM/ZIP/APPSOURCE
  '220': {
    useNumericPhone: false,
    makeParams: (d, programCode) => ({
      FNAME: d.FNAME,
      LNAME: d.LNAME,
      EMAIL: d.EMAIL,
      PHONE: d.PHONE,
      ZIP: d.ZIP,
      PROGRAM: programCode || '',
      APPSOURCE: 'affiliate',
    }),
  },
  '1025': {
    useNumericPhone: false,
    makeParams: (d, programCode) => ({
      FNAME: d.FNAME,
      LNAME: d.LNAME,
      EMAIL: d.EMAIL,
      PHONE: d.PHONE,
      ZIP: d.ZIP,
      PROGRAM: programCode || '',
      APPSOURCE: 'affiliate',
    }),
  },
};

// single builder that uses the strategy (falls back to raw linkout for other schools)
const buildDualCtaUrl = async (
  baseUrl: string,
  schoolId: string,
  formInputs: any,
  programCode?: string
): Promise<string> => {
  const strat = SCHOOL_STRATEGY[schoolId];
  if (!strat) return baseUrl; // unchanged behavior: open raw link if no strategy

  const d = await getFormData(formInputs, strat.useNumericPhone);
  return buildUrlFromParams(baseUrl, strat.makeParams(d, programCode));
};

const useDualCtaTest = (schoolId: string = '', programCode: string | null = null): ReturnType => {
  const viewCorrelationIdMap = useSelector(selectViewCorrelationIdMap);
  const formInputs = useSelector(selectAllInputs);

  const { elementClicked, elementViewed } = useElementEvents({
    correlationId: viewCorrelationIdMap[schoolId],
  });

  const { isTestEnabled, dualCtaText, dualCtaEnabled, dualCtaLinkout } = useStartYourApplicationEditableAsset(schoolId);

  const DualCtaTestComponent: FC<DualCtaTestProps> = ({ schoolId, className }) => {
    const isDualCtaTest = isTestEnabled && dualCtaEnabled;

    const applicationCtaBtn = useMemo(
      () => ({
        webElement: {
          elementType: 'linkout-cta',
          text: dualCtaText,
          location: 'drawer',
          htmlId: 'voyager-schedule-call-cta',
          name: 'application-cta',
        },
      }),
      []
    );

    const onDualCtaClick = async () => {
      let outbound = dualCtaLinkout || '';

      if (dualCtaLinkout && formInputs) {
        try {
          outbound = await buildDualCtaUrl(dualCtaLinkout, schoolId, formInputs, programCode ?? undefined);
        } catch {
          outbound = dualCtaLinkout || '';
        }
      }

      console.log('outbound is: ', outbound);

      window.open(outbound, '_blank', 'noopener,noreferrer');
      elementClicked({ ...applicationCtaBtn, outboundUrl: outbound });
    };

    useEffect(() => {
      if (applicationCtaBtn) {
        elementViewed(applicationCtaBtn);
      }
    }, [isDualCtaTest, applicationCtaBtn]);

    if (isDualCtaTest) {
      return (
        <button type="button" className={className || ''} onClick={onDualCtaClick}>
          {dualCtaText}
        </button>
      );
    }

    return <></>;
  };

  return {
    DualCtaTestComponent,
    shouldApplyWrapperStyles: (isTestEnabled && dualCtaEnabled) || false,
  };
};

export default useDualCtaTest;
